import { addDays, type BusinessCalendar, type DateOnly } from '@/domain/dates'
import { restWindow } from './calendar'

export interface PeriodSuggestion {
  /** Vacation days to request (first and last working day). */
  start: DateOnly
  end: DateOnly
  workingDays: number
  /** Consecutive days off, including the weekends and holidays around the period. */
  restFrom: DateOnly
  restTo: DateOnly
  restDays: number
  /** restDays / workingDays: how much rest each vacation day buys. */
  ratio: number
  /** Holiday names that make this period worth it. */
  holidays: string[]
  kind: 'bridge' | 'long'
}

function nthBusinessDay(cal: BusinessCalendar, start: DateOnly, n: number): DateOnly {
  return n <= 1 ? start : cal.addBusinessDays(start, n - 1)
}

function candidate(cal: BusinessCalendar, start: DateOnly, workingDays: number, kind: PeriodSuggestion['kind']): PeriodSuggestion {
  const end = nthBusinessDay(cal, start, workingDays)
  const w = restWindow(cal, start, end)
  // Only holidays on working days add rest (a holiday on a Sunday adds nothing).
  const holidays = cal.holidaysBetween(w.from, w.to).filter((h) => !cal.isWeekend(h.date)).map((h) => h.name)
  return { start, end, workingDays, restFrom: w.from, restTo: w.to, restDays: w.days, ratio: w.days / workingDays, holidays, kind }
}

const overlaps = (a: PeriodSuggestion, b: PeriodSuggestion) => a.restFrom <= b.restTo && b.restFrom <= a.restTo

/**
 * "Pontes": short periods (1–4 working days) next to a holiday that turn a few vacation
 * days into a long break. Only periods that beat a normal week (ratio > 7/5) and touch a
 * holiday are kept; the best one per holiday cluster wins.
 */
export function suggestBridges(cal: BusinessCalendar, from: DateOnly, to: DateOnly, maxDays = 4): PeriodSuggestion[] {
  const found: PeriodSuggestion[] = []
  for (let d = cal.nextBusinessDay(from); d <= to; d = cal.addBusinessDays(d, 1)) {
    for (let k = 1; k <= maxDays; k += 1) {
      const c = candidate(cal, d, k, 'bridge')
      if (c.end > to || c.holidays.length === 0) continue
      if (c.ratio <= 7 / 5 + 0.01) continue
      found.push(c)
    }
  }
  found.sort((a, b) => b.ratio - a.ratio || a.workingDays - b.workingDays || (a.start < b.start ? -1 : 1))
  const picked: PeriodSuggestion[] = []
  for (const c of found) if (!picked.some((p) => overlaps(p, c))) picked.push(c)
  return picked.sort((a, b) => (a.start < b.start ? -1 : 1))
}

/**
 * Best blocks of exactly `workingDays` consecutive working days (the legal 10-day period),
 * best first: longest rest for the same days, then earliest. `avoid` lets the caller skip days
 * that are blocked (team limit reached, already booked).
 */
export function suggestLongPeriods(
  cal: BusinessCalendar,
  from: DateOnly,
  to: DateOnly,
  workingDays: number,
  avoid: (day: DateOnly) => boolean = () => false,
  limit = 3,
): PeriodSuggestion[] {
  const found: PeriodSuggestion[] = []
  for (let d = cal.nextBusinessDay(from); d <= to; d = cal.addBusinessDays(d, 1)) {
    const c = candidate(cal, d, workingDays, 'long')
    if (c.end > to) break
    let blocked = false
    for (let x = c.start; x <= c.end; x = addDays(x, 1)) if (avoid(x)) blocked = true
    if (!blocked) found.push(c)
  }
  found.sort((a, b) => b.restDays - a.restDays || (a.start < b.start ? -1 : 1))
  const picked: PeriodSuggestion[] = []
  for (const c of found) {
    if (picked.length >= limit) break
    if (!picked.some((p) => overlaps(p, c))) picked.push(c)
  }
  return picked // best first: the UI recommends the first one
}
